import React, {useEffect, useState} from 'react'
import {Link} from 'react-router-dom'
import styled from 'styled-components'
import type {AppLocale} from 'i18n/locales'
import {LOCALE_FLAGS, LOCALE_LABELS, SUPPORTED_LOCALES} from 'i18n/locales'
import {usePromoOfferCopy, type PromoOfferKey} from './usePromoOfferCopy'

const BRAND_LOGO_SRC = '/logo-dark.svg'

const iconProps = {width: 20, height: 20, viewBox: '0 0 24 24', fill: '#fff'} as const

function LiveIcon() {
  return (
    <svg {...iconProps} aria-hidden="true">
      <path d="M4.5 4.5a3 3 0 00-3 3v9a3 3 0 003 3h8.25a3 3 0 003-3v-9a3 3 0 00-3-3H4.5zM19.94 18.75l-2.69-2.69V7.94l2.69-2.69c.944-.945 2.56-.276 2.56 1.06v11.38c0 1.336-1.616 2.005-2.56 1.06z" />
    </svg>
  )
}

function TicketIcon() {
  return (
    <svg {...iconProps} aria-hidden="true">
      <path d="M4.5 3.75a3 3 0 00-3 3v.75c0 .414.336.75.75.75a2.25 2.25 0 010 4.5.75.75 0 00-.75.75v.75a3 3 0 003 3h15a3 3 0 003-3v-.75a.75.75 0 00-.75-.75 2.25 2.25 0 010-4.5.75.75 0 00.75-.75v-.75a3 3 0 00-3-3h-15z" />
    </svg>
  )
}

function CertificateIcon() {
  return (
    <svg {...iconProps} aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.603 3.799A4.49 4.49 0 0112 2.25c1.357 0 2.573.6 3.397 1.549a4.49 4.49 0 013.498 1.307 4.491 4.491 0 011.307 3.497A4.49 4.49 0 0121.75 12a4.49 4.49 0 01-1.549 3.397 4.491 4.491 0 01-1.307 3.497 4.491 4.491 0 01-3.497 1.307A4.49 4.49 0 0112 21.75a4.49 4.49 0 01-3.397-1.549 4.49 4.49 0 01-3.498-1.306 4.491 4.491 0 01-1.307-3.498A4.49 4.49 0 012.25 12c0-1.357.6-2.573 1.549-3.397a4.49 4.49 0 011.307-3.497 4.49 4.49 0 013.497-1.307zm7.007 6.387a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
      />
    </svg>
  )
}

function ChartIcon() {
  return (
    <svg {...iconProps} aria-hidden="true">
      <path d="M18.375 2.25c-1.035 0-1.875.84-1.875 1.875v15.75c0 1.035.84 1.875 1.875 1.875h.75c1.035 0 1.875-.84 1.875-1.875V4.125c0-1.036-.84-1.875-1.875-1.875h-.75zM9.75 8.625c0-1.036.84-1.875 1.875-1.875h.75c1.036 0 1.875.84 1.875 1.875v11.25c0 1.035-.84 1.875-1.875 1.875h-.75a1.875 1.875 0 01-1.875-1.875V8.625zM3 13.125c0-1.036.84-1.875 1.875-1.875h.75c1.036 0 1.875.84 1.875 1.875v6.75c0 1.035-.84 1.875-1.875 1.875h-.75A1.875 1.875 0 013 19.875v-6.75z" />
    </svg>
  )
}

let heroGlowUid = 0

function HeroGlowArt() {
  const [id] = useState(() => `promoHeroGlow${++heroGlowUid}`)
  return (
    <HeroGlow viewBox="0 0 600 260" fill="none" aria-hidden="true">
      <defs>
        <radialGradient id={id} cx="50%" cy="0%" r="75%">
          <stop offset="0%" stopColor="#f7426f" stopOpacity="0.16" />
          <stop offset="55%" stopColor="#f87a58" stopOpacity="0.08" />
          <stop offset="100%" stopColor="#f87a58" stopOpacity="0" />
        </radialGradient>
      </defs>
      <ellipse cx="300" cy="40" rx="300" ry="180" fill={`url(#${id})`} />
      <g opacity="0.35" stroke="#f7426f" strokeWidth="1.4" fill="none">
        <circle cx="300" cy="10" r="60" strokeDasharray="2 8" strokeLinecap="round" />
        <circle cx="300" cy="10" r="96" strokeDasharray="2 10" strokeLinecap="round" />
      </g>
    </HeroGlow>
  )
}

const Root = styled.div`
  box-sizing: border-box;
  padding-bottom: 64px;
  background: linear-gradient(
    165deg,
    var(--clr-bg-1, #fffcfb) 0%,
    var(--clr-bg-pink, #fff6f9) 38%,
    var(--clr-common-white, #ffffff) 100%
  );
  color: var(--clr-common-black, #0d1c42);
`

const Inner = styled.div`
  max-width: 820px;
  margin: 0 auto;
  padding: 0 18px;
`

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 20px 0 8px;
`

const Wordmark = styled.span`
  display: inline-flex;
  align-items: center;
`

const WordmarkLogo = styled.img`
  height: 34px;
  width: auto;
  display: block;
`

const LangBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-end;
`

const LangBtn = styled.button<{$active?: boolean}>`
  border-radius: 999px;
  border: 1px solid
    ${(p) =>
      p.$active
        ? 'var(--clr-theme-1, #f7426f)'
        : 'var(--clr-border-4, #e6e6e6)'};
  background: ${(p) =>
    p.$active ? 'rgba(247, 66, 111, 0.08)' : 'var(--clr-common-white, #fff)'};
  padding: 6px 11px;
  font-size: 12px;
  cursor: pointer;
  color: var(--clr-text-3, #333);
`

const Hero = styled.div`
  position: relative;
  text-align: center;
  padding: 12px 0 8px;
`

const HeroGlow = styled.svg`
  position: absolute;
  top: -30px;
  left: 50%;
  transform: translateX(-50%);
  width: min(680px, 130%);
  height: auto;
  z-index: 0;
  pointer-events: none;
`

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
`

const Eyebrow = styled.p`
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--clr-theme-1, #f7426f);
  margin: 0 0 14px;
`

const H1 = styled.h1`
  font-size: clamp(1.9rem, 5vw, 2.65rem);
  font-weight: 800;
  line-height: 1.15;
  margin: 0 0 16px;
  color: var(--clr-common-black, #0d1c42);
`

const OfferName = styled.p`
  display: inline-block;
  font-weight: 700;
  font-size: 1.05rem;
  margin: 0 0 14px;
  padding: 7px 16px;
  border-radius: 999px;
  background: rgba(247, 66, 111, 0.08);
  color: var(--clr-theme-1, #f7426f);
`

const Lead = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: var(--clr-text-3, #333);
  max-width: 640px;
  margin: 0 auto 28px;
`

const TrustRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px 12px;
  margin-bottom: 8px;
`

const TrustChip = styled.span`
  font-size: 12px;
  padding: 7px 12px;
  border-radius: 999px;
  background: var(--clr-common-white, #fff);
  border: 1px solid var(--clr-border-4, #e6e6e6);
  box-shadow: 0 4px 14px rgba(13, 28, 66, 0.05);
`

const Section = styled.section`
  margin: 48px 0;
`

const SecTitleCenter = styled.h2`
  text-align: center;
  font-size: clamp(1.3rem, 3vw, 1.6rem);
  font-weight: 800;
  margin: 0 0 14px;
  color: var(--clr-common-black, #0d1c42);
`

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 16px;
  margin-top: 24px;
`

const FeatureCard = styled.div`
  padding: 20px 18px;
  border-radius: 12px;
  background: var(--clr-common-white, #fff);
  border: 1px solid var(--clr-border-4, #e6e6e6);
  box-shadow: 0 8px 24px rgba(13, 28, 66, 0.05);
`

const FeatureIcon = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  margin-bottom: 12px;
  background: var(
    --clr-gradient-14,
    linear-gradient(90deg, #f87a58, #f7426f, #f7426f, #f87a58)
  );
  color: #fff;
`

const FeatureTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  margin: 0 0 6px;
  color: var(--clr-common-black, #0d1c42);
`

const FeatureBody = styled.p`
  font-size: 0.94rem;
  line-height: 1.55;
  color: var(--clr-text-3, #333);
  margin: 0;
`

const StepsList = styled.div`
  display: grid;
  gap: 14px;
  margin-top: 24px;
`

const StepRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  padding: 16px 18px;
  border-radius: 12px;
  background: var(--clr-common-white, #fff);
  border: 1px solid var(--clr-border-4, #e6e6e6);
`

const StepNum = styled.div`
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 14px;
  color: #fff;
  background: var(
    --clr-gradient-14,
    linear-gradient(90deg, #f87a58, #f7426f, #f7426f, #f87a58)
  );
`

const StepText = styled.div`
  h3 {
    margin: 0 0 4px;
    font-size: 1rem;
    font-weight: 700;
    color: var(--clr-common-black, #0d1c42);
  }
  p {
    margin: 0;
    font-size: 0.94rem;
    line-height: 1.55;
    color: var(--clr-text-3, #333);
  }
`

const FaqItem = styled.div`
  margin-bottom: 12px;
  padding: 16px 18px;
  border-radius: 10px;
  border: 1px solid var(--clr-border-4, #e6e6e6);
  background: var(--clr-common-white, #fff);
  h3 {
    margin: 0 0 8px;
    font-size: 1rem;
    font-weight: 700;
  }
  p {
    margin: 0;
    font-size: 0.95rem;
    line-height: 1.6;
    color: var(--clr-text-3, #333);
  }
`

const PriceBox = styled.div`
  margin: 0 auto;
  padding: 26px 22px 20px;
  border-radius: 14px;
  background: var(--clr-common-white, #ffffff);
  border: 1px solid var(--clr-border-4, #e6e6e6);
  box-shadow: 0 12px 36px rgba(13, 28, 66, 0.08);
  text-align: center;
`

const TrustSectionBody = styled.p`
  text-align: center;
  font-size: 1rem;
  line-height: 1.7;
  color: var(--clr-text-3, #333);
  max-width: 680px;
  margin: 0 auto;
`

const ClosingBox = styled.div`
  text-align: center;
  padding: 34px 22px;
  border-radius: 16px;
  background: var(
    --clr-gradient-14,
    linear-gradient(120deg, #f87a58, #f7426f)
  );
  color: #fff;
`

const ClosingTitle = styled.h2`
  font-size: clamp(1.3rem, 3vw, 1.6rem);
  font-weight: 800;
  margin: 0 0 10px;
  color: #fff;
`

const ClosingBody = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  margin: 0 auto 22px;
  max-width: 520px;
  color: rgba(255, 255, 255, 0.92);
`

const ClosingBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 26px;
  min-height: 52px;
  border-radius: 8px;
  font-weight: 700;
  text-decoration: none;
  background: #fff;
  color: var(--clr-theme-1, #f7426f) !important;
`

const PriceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
  margin-top: 18px;
  text-align: center;
  font-variant-numeric: tabular-nums;
  span {
    display: block;
    font-size: 12px;
    color: var(--clr-text-5, #7c7c7c);
    margin-bottom: 4px;
  }
  strong {
    font-size: 1.05rem;
    color: var(--clr-theme-1, #f7426f);
  }
`

const CtaRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 22px;
  & > a {
    width: 100%;
    max-width: 320px;
  }
`

const Btn = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 24px;
  min-height: 52px;
  border-radius: 8px;
  font-weight: 700;
  text-decoration: none;
  text-align: center;
  border: 0;
  background: var(
    --clr-gradient-14,
    linear-gradient(90deg, #f87a58, #f7426f, #f7426f, #f87a58)
  );
  color: #fff !important;
  box-shadow: 0 6px 20px rgba(247, 66, 111, 0.25);
`

const Hint = styled.div<{$bad?: boolean}>`
  margin: 0 auto 16px;
  max-width: 620px;
  padding: 12px 14px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.5;
  border: 1px solid
    ${(p) =>
      p.$bad ? 'rgba(220, 38, 38, 0.45)' : 'var(--clr-border-4, #e6e6e6)'};
  background: ${(p) => (p.$bad ? 'rgba(254, 226, 226, 0.55)' : '#fff')};
  color: ${(p) => (p.$bad ? '#7f1d1d' : 'var(--clr-text-3, #333)')};
`

const Foot = styled.p`
  text-align: center;
  font-size: 13px;
  color: var(--clr-text-5, #7c7c7c);
  margin-top: 40px;
  line-height: 1.6;
`

function formatCountdown(ms: number, locale: AppLocale, t: (k: PromoOfferKey) => string): string {
  if (ms <= 0) {
    return t('offer.countdownExpired')
  }
  const s = Math.floor(ms / 1000)
  const d = Math.floor(s / 86400)
  const h = Math.floor((s % 86400) / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  if (locale === 'bg') {
    if (d > 0) {
      return `${d} дни ${h} ч ${m} мин ${sec} сек`
    }
    if (h > 0) {
      return `${h} ч ${m} мин ${sec} сек`
    }
    if (m > 0) {
      return `${m} мин ${sec} сек`
    }
    return `${sec} сек`
  }
  if (d > 0) {
    return `${d}d ${h}h ${m}m ${sec}s`
  }
  if (h > 0) {
    return `${h}h ${m}m ${sec}s`
  }
  if (m > 0) {
    return `${m}m ${sec}s`
  }
  return `${sec}s`
}

function PromoInviteCountdown(props: {
  expiresAtIso: string
  locale: AppLocale
  t: (k: PromoOfferKey) => string
}) {
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const id = window.setInterval(() => setTick((n) => n + 1), 1000)
    return () => window.clearInterval(id)
  }, [props.expiresAtIso])
  const end = new Date(props.expiresAtIso).getTime()
  const ms = end - Date.now()
  void tick
  return (
    <div className="org-promo-landing-countdown" role="status" style={{textAlign: 'center', marginBottom: 18}}>
      {props.t('offer.countdownPrefix')}
      <strong>{formatCountdown(ms, props.locale, props.t)}</strong>
    </div>
  )
}

export type PromoOfferEconomy = {
  event_price_per_day: number
  promo_event_price_per_day: number | null
  display_event_price_per_day: number
  has_event_day_promo: boolean
  min_ticket_fee: number
  ticket_fee_percent: number
  min_product_fee: number
  product_fee_percent: number
}

export function PromoOfferLongPage(props: {
  offerName: string
  pageLocale: AppLocale
  setPageLocale: (l: AppLocale) => void
  economy: PromoOfferEconomy
  inviteFromUrl: string | null
  inviteExpiresAt: string | null
  registerWithInviteTo: string | null
}) {
  const {t} = usePromoOfferCopy(props.pageLocale)
  const offerActive = Boolean(props.inviteExpiresAt)
  const inviteInvalid = Boolean(props.inviteFromUrl && !props.inviteExpiresAt)
  const econ = props.economy
  const list = econ.event_price_per_day
  const promo = econ.promo_event_price_per_day
  const hasPromo =
    Boolean(econ.has_event_day_promo) &&
    promo != null &&
    list != null &&
    promo < list

  const cta = props.registerWithInviteTo ? (
    <CtaRow>
      <Btn to={props.registerWithInviteTo}>{t('offer.ctaPrimary')}</Btn>
    </CtaRow>
  ) : (
    <Hint style={{marginTop: 22}}>{t('offer.ctaNoTokenHint')}</Hint>
  )

  return (
    <Root>
      <Inner>
        <TopBar>
          <Wordmark>
            <WordmarkLogo src={BRAND_LOGO_SRC} alt="Eventa.tv" />
          </Wordmark>
          <LangBar>
            {SUPPORTED_LOCALES.map((loc) => (
              <LangBtn
                key={loc}
                type="button"
                $active={loc === props.pageLocale}
                onClick={() => props.setPageLocale(loc)}
              >
                {LOCALE_FLAGS[loc]} {LOCALE_LABELS[loc]}
              </LangBtn>
            ))}
          </LangBar>
        </TopBar>

        {inviteInvalid ? <Hint $bad>{t('offer.inviteInvalid')}</Hint> : null}
        {!props.inviteFromUrl && !offerActive ? (
          <Hint>{t('offer.invitePublicOnly')}</Hint>
        ) : null}
        {offerActive && props.inviteExpiresAt ? (
          <PromoInviteCountdown
            expiresAtIso={props.inviteExpiresAt}
            locale={props.pageLocale}
            t={t}
          />
        ) : null}

        <Hero>
          <HeroGlowArt />
          <HeroContent>
            <Eyebrow>{t('offer.heroEyebrow')}</Eyebrow>
            <H1>{t('offer.heroTitle')}</H1>
            <OfferName>{props.offerName}</OfferName>
            <Lead>{t('offer.heroLead').split('{name}').join(props.offerName)}</Lead>
            <TrustRow>
              <TrustChip>{t('offer.trust1')}</TrustChip>
              <TrustChip>{t('offer.trust2')}</TrustChip>
              <TrustChip>{t('offer.trust3')}</TrustChip>
              <TrustChip>{t('offer.trust4')}</TrustChip>
            </TrustRow>
            {cta}
          </HeroContent>
        </Hero>

        <Section>
          <SecTitleCenter>{t('offer.featuresTitle')}</SecTitleCenter>
          <FeatureGrid>
            <FeatureCard>
              <FeatureIcon>
                <LiveIcon />
              </FeatureIcon>
              <FeatureTitle>{t('offer.feature1Title')}</FeatureTitle>
              <FeatureBody>{t('offer.feature1Body')}</FeatureBody>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon>
                <TicketIcon />
              </FeatureIcon>
              <FeatureTitle>{t('offer.feature2Title')}</FeatureTitle>
              <FeatureBody>{t('offer.feature2Body')}</FeatureBody>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon>
                <CertificateIcon />
              </FeatureIcon>
              <FeatureTitle>{t('offer.feature3Title')}</FeatureTitle>
              <FeatureBody>{t('offer.feature3Body')}</FeatureBody>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon>
                <ChartIcon />
              </FeatureIcon>
              <FeatureTitle>{t('offer.feature4Title')}</FeatureTitle>
              <FeatureBody>{t('offer.feature4Body')}</FeatureBody>
            </FeatureCard>
          </FeatureGrid>
        </Section>

        <Section>
          <SecTitleCenter>{t('offer.howTitle')}</SecTitleCenter>
          <StepsList>
            <StepRow>
              <StepNum>1</StepNum>
              <StepText>
                <h3>{t('offer.step1Title')}</h3>
                <p>{t('offer.step1Body')}</p>
              </StepText>
            </StepRow>
            <StepRow>
              <StepNum>2</StepNum>
              <StepText>
                <h3>{t('offer.step2Title')}</h3>
                <p>{t('offer.step2Body')}</p>
              </StepText>
            </StepRow>
            <StepRow>
              <StepNum>3</StepNum>
              <StepText>
                <h3>{t('offer.step3Title')}</h3>
                <p>{t('offer.step3Body')}</p>
              </StepText>
            </StepRow>
          </StepsList>
        </Section>

        <Section>
          <PriceBox>
            <SecTitleCenter style={{marginBottom: 4}}>
              {t('offer.priceTitle')}
            </SecTitleCenter>
            <p style={{margin: '0 0 8px', fontSize: 14, color: '#666'}}>
              {props.offerName}
            </p>
            {hasPromo ? (
              <p style={{fontSize: 26, fontWeight: 800}}>
                <span style={{textDecoration: 'line-through', color: '#aaa', marginRight: 10}}>
                  {Number(list).toFixed(2)} €
                </span>
                <span style={{color: 'var(--clr-theme-1, #f7426f)'}}>
                  {Number(promo).toFixed(2)} €
                </span>
                <span style={{display: 'block', fontSize: 13, fontWeight: 500, marginTop: 6}}>
                  {t('offer.priceDay')}
                </span>
              </p>
            ) : (
              <p style={{fontSize: 26, fontWeight: 800, color: 'var(--clr-theme-1, #f7426f)'}}>
                {Number(econ.display_event_price_per_day).toFixed(2)} €
                <span style={{display: 'block', fontSize: 13, fontWeight: 500, marginTop: 6}}>
                  {t('offer.priceDay')}
                </span>
              </p>
            )}
            <PriceGrid>
              <div>
                <span>{t('offer.priceTicketMin')}</span>
                <strong>{econ.min_ticket_fee.toFixed(2)} €</strong>
              </div>
              <div>
                <span>{t('offer.priceTicketPct')}</span>
                <strong>{econ.ticket_fee_percent.toFixed(2)}%</strong>
              </div>
              <div>
                <span>{t('offer.priceProdMin')}</span>
                <strong>{econ.min_product_fee.toFixed(2)} €</strong>
              </div>
              <div>
                <span>{t('offer.priceProdPct')}</span>
                <strong>{econ.product_fee_percent.toFixed(2)}%</strong>
              </div>
            </PriceGrid>
            {cta}
          </PriceBox>
        </Section>

        <Section>
          <SecTitleCenter>{t('offer.faqHeading')}</SecTitleCenter>
          <FaqItem>
            <h3>{t('offer.faq1q')}</h3>
            <p>{t('offer.faq1a')}</p>
          </FaqItem>
          <FaqItem>
            <h3>{t('offer.faq2q')}</h3>
            <p>{t('offer.faq2a')}</p>
          </FaqItem>
          <FaqItem>
            <h3>{t('offer.faq3q')}</h3>
            <p>{t('offer.faq3a')}</p>
          </FaqItem>
          <FaqItem>
            <h3>{t('offer.faq4q')}</h3>
            <p>{t('offer.faq4a')}</p>
          </FaqItem>
          <FaqItem>
            <h3>{t('offer.faq5q')}</h3>
            <p>{t('offer.faq5a')}</p>
          </FaqItem>
        </Section>

        <Section>
          <SecTitleCenter>{t('offer.trustSectionTitle')}</SecTitleCenter>
          <TrustSectionBody>{t('offer.trustSectionBody')}</TrustSectionBody>
        </Section>

        {props.registerWithInviteTo ? (
          <Section style={{marginBottom: 0}}>
            <ClosingBox>
              <ClosingTitle>{t('offer.closingTitle')}</ClosingTitle>
              <ClosingBody>
                {t('offer.closingBody').split('{name}').join(props.offerName)}
              </ClosingBody>
              <ClosingBtn to={props.registerWithInviteTo}>{t('offer.ctaPrimary')}</ClosingBtn>
            </ClosingBox>
          </Section>
        ) : null}

        <Foot>{t('offer.footerNote')}</Foot>
      </Inner>
    </Root>
  )
}
